import { DatePipe } from '@angular/common';
import { finalize } from 'rxjs';
import {
    ChangeDetectionStrategy, Component,
    inject, model, ModelSignal,
    OnInit, Signal, signal, viewChild, WritableSignal,
} from '@angular/core';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';

/**
 * models / interfaces
 */
import { IUnitTypeModel } from '../../models';

/**
 * consts
*/
import { UnitTypesConst } from '../../consts';
import { UnitTypesListTranslateMapper } from './consts';

/**
 * services
 */
import { UnitTypeService } from '../../services/unit-type.service';

/**
 * utilities
 */
import { TruncatePipe } from '@core/utilities';

/**
 * shared
*/
import { CardComponent } from '@sharedComponents/card/card.component';
import { BadgeDirective } from '@sharedDirectives/badge/badge.directive';
import { ModalComponent } from '@sharedComponents/modal/modal.component';
import { ButtonDirective } from '@sharedDirectives/button/button.directive';
import { DropdownComponent } from '@sharedComponents/dropdown/dropdown.component';
import { IDropdownItem } from '@sharedComponents/dropdown/interfaces/IDropdownItem';
import { TemplateDirective } from '@shared/directives/templates/template.directive';
import { ListGroupDirective } from '@shared/directives/list-groupe/list-group.directive';
import { WavesEffectDirective } from '@sharedDirectives/node-waves/waves-effect.directive';
import { UnitTypeFormComponent } from '../../components/unit-type-form/unit-type-form.component';
import { ListGroupItemDirective } from '@shared/directives/list-groupe/list-group-item.directive';
import { SpinKitWaveComponent } from '@sharedComponents/spinner/spinkit/components/spin-kit-wave/spin-kit-wave.component';
import { ITableColumn, TableComponent, TableModule, TableSortableColumnDirective, TableStateService } from '@sharedModules/tables';

@Component({
    selector: 'app-unit-types-list',
    templateUrl: './unit-types-list.component.html',
    styleUrl: './unit-types-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DatePipe,
        /**
         * libs
         */
        TruncatePipe,
        TranslatePipe,
        /**
         * directives
         */
        BadgeDirective,
        ListGroupDirective,
        ButtonDirective,
        TemplateDirective,
        WavesEffectDirective,
        ListGroupItemDirective,
        TableSortableColumnDirective,
        /**
         * shared
         */
        TableModule,
        CardComponent,
        ModalComponent,
        DropdownComponent,
        SpinKitWaveComponent,
        UnitTypeFormComponent,
    ],
})
export class UnitTypesListComponent implements OnInit {

    private readonly _unitTypeService = inject(UnitTypeService);

    public readonly tableState = viewChild<TableComponent, TableStateService>(TableComponent, { read: TableStateService });

    /** Reference to the embedded form component — used to delegate form operations. */
    public readonly formRef = viewChild.required(UnitTypeFormComponent);

    /** Module constants. */
    public readonly UNIT_TYPES_CONST = UnitTypesConst;

    /** Translate key mapper. */
    public readonly TRANSLATE_KEY_MAP = UnitTypesListTranslateMapper;

    /** Table column definitions. */
    public tableColumns: ITableColumn<any>[] = [];

    /** Full list signal. */
    public readonly unitTypesList: WritableSignal<IUnitTypeModel[]> = signal<IUnitTypeModel[]>([]);

    /** Currently selected row (delete modal). */
    public readonly selectedUnitType: WritableSignal<IUnitTypeModel | null> = signal<IUnitTypeModel | null>(null);

    /** Form modal visibility. */
    public readonly formModal: ModelSignal<boolean> = model<boolean>(false);

    /** Delete modal visibility. */
    public readonly deleteModal: ModelSignal<boolean> = model<boolean>(false);

    private readonly _loading: WritableSignal<boolean> = signal<boolean>(false);
    public  readonly loading:  Signal<boolean>         = this._loading.asReadonly();

    public ngOnInit(): void {
        this.tableColumns = [
            {
                id: 'name',
                field: 'name',
                searchBy: 'name',
                sortable: true,
                header: this.TRANSLATE_KEY_MAP.DATA_TABLE.COLUMNS.NAME(),
            },
            {
                id: 'description',
                field: 'description',
                searchBy: 'description',
                sortable: true,
                header: this.TRANSLATE_KEY_MAP.DATA_TABLE.COLUMNS.DESCRIPTION(),
            },
            {
                id: 'level',
                field: 'level',
                sortable: true,
                searchBy: 'level',
                header: this.TRANSLATE_KEY_MAP.DATA_TABLE.COLUMNS.LEVEL(),
            },
            {
                id: 'defaultTypeForItsLevel',
                field: 'defaultTypeForItsLevel',
                sortable: true,
                header: this.TRANSLATE_KEY_MAP.DATA_TABLE.COLUMNS.DEFAULT_TYPE_FOR_ITS_LEVEL(),
            },
            {
                id: 'icon',
                field: 'icon',
                searchBy: 'icon',
                header: this.TRANSLATE_KEY_MAP.DATA_TABLE.COLUMNS.ICON(),
            },
            {
                id: 'createdOn',
                field: 'createdOn',
                sortable: true,
                header: this.TRANSLATE_KEY_MAP.DATA_TABLE.COLUMNS.CREATED_ON(),
            },
            {
                id: 'actions',
                header: this.TRANSLATE_KEY_MAP.DATA_TABLE.COLUMNS.ACTIONS(),
                headerClassStyle: 'justify-content-center',
            },
        ];
    }

    /** Load / reload table data. */
    public onTableInitialized(): void {
        this.tableState()?.setLoading(true);
        this._unitTypeService
            .getList()
            .pipe(finalize(() => this.tableState()?.setLoading(false)))
            .subscribe((list) => this.unitTypesList.set(list ?? []));
    }

    /** Row actions dropdown items. */
    public getRowActions(row: IUnitTypeModel): IDropdownItem[] {
        return [
            {
                label: 'GENERAL.EDIT',
                icon: 'ti ti-pencil icon-base',
                command: () => this._showEditForm(row),
            },
            {
                label: 'GENERAL.DELETE',
                icon: 'ti ti-trash icon-base',
                addDivider: true,
                classStyle: 'text-danger',
                command: () => {
                    this.selectedUnitType.set(row);
                    this.toggleDeleteModal();
                },
            },
        ];
    }

    // ─── Form modal ───────────────────────────────────────────────────────────

    public showCreateForm(): void {
        this.formRef().initCreate();
        this.formModal.set(true);
    }

    private _showEditForm(row: IUnitTypeModel): void {
        this.selectedUnitType.set(row);
        this.formRef().initEdit(row.id);
        this.formModal.set(true);
    }

    public toggleFormModal(): void {
        this.formRef().cancel();
        this.selectedUnitType.set(null);
        this.formModal.update((v) => !v);
    }

    public onFormSubmitted(): void {
        this.formModal.set(false);
        this.onTableInitialized();
    }

    // ─── Delete modal ─────────────────────────────────────────────────────────

    public toggleDeleteModal(): void {
        this.deleteModal.update((v) => !v);
    }

    public deleteUnitType(): void {
        const id = this.selectedUnitType()?.id;
        if (id == null) throw new Error('Unit Type ID is required for delete');

        this._loading.set(true);

        this._unitTypeService
            .delete(id)
            .pipe(
                finalize(() => {
                    this._loading.set(false);
                    this.toggleDeleteModal();
                    this.onTableInitialized();
                }),
            )
            .subscribe();
    }

}
